import { Injectable, isDevMode, OnDestroy } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { AzureLogin, AzureLogout, SetAuthData, SetNotification, SetSelectedRoute } from '../store/api-eco.actions';
import { ApiEcoState } from '../store/api-eco.state';
import { AuthData, User } from '../store';
import { v4 as uuidv4 } from 'uuid';
import { environment } from 'src/environments/environment';
import { ProgressSpinnerDialogService } from './progress-spinner-dialog.service';
import { ContextStatusType, ContextUpdateType, UserContextService } from './user-context.service';
import { APIELogService } from './apie-log.service';
import { AlertMessages } from 'src/assets/data/alert-messages';

@Injectable({
  providedIn: 'root',
})
export class HttpInterceptorService implements HttpInterceptor, OnDestroy {
  private destroyed = new Subject();
  authData: AuthData;
  authUser: User;
  apiUrl: string;
  b2cPolicies: any;
  constructor(
    private store: Store,
    private progressDialogService: ProgressSpinnerDialogService,
    private userContextService: UserContextService,
    private logService: APIELogService
  ) {
    this.b2cPolicies = environment.b2cPolicies;
    this.apiUrl = environment.apiUrl;
    this.store
      .select(ApiEcoState.getAuthData)
      .pipe(takeUntil(this.destroyed))
      .subscribe((authData: AuthData) => {
        this.authData = authData;
      });
    this.store
      .select(ApiEcoState.getUser)
      .pipe(takeUntil(this.destroyed))
      .subscribe((user: User) => {
        this.authUser = user;
      });
  }
  getAdditionalHeaders(req: HttpRequest<any>): any {
    const headers = { 'x-correlation-id': uuidv4() };
    const persistedToken = this.userContextService.getTokenInfo()?.token;
    const userToken = this.authUser?.userToken || persistedToken;
    const shouldAttachUserHeader = this.shouldAttachUserHeader(req);

    if (userToken && shouldAttachUserHeader) {
      // Check for expired token
      this.checkOrRefreshToken(userToken);
      headers['x-user'] = userToken;
    }
    try {
      if (req.headers && req.headers?.has('x-user')) {
        headers['x-user'] = req.headers?.get('x-user');
      }
    } catch (error) {
      console.log(error, 'error adding reu headers X-USER in http interceptor');
    }
    return headers;
  }

  private shouldAttachUserHeader(req: HttpRequest<any>): boolean {
    const url = (req?.url || '').toLowerCase();

    // Login must not send x-user; backend establishes user context for this call.
    return !url.includes('/users/login');
  }

  checkOrRefreshToken(token: string) {
    const tokenInfo = this.userContextService.getTokenInfo();
    // tokenExpiry stored as plain number in seconds — cast to Number to handle any legacy stringified values.
    const savedTokenExpiry = Number(tokenInfo.tokenExpiry);

    if (savedTokenExpiry && savedTokenExpiry < Math.floor(Date.now() / 1000)) {
      const context = this.userContextService.getContext();

      // Do not stack another redirect if login is already in progress.
      if (context.global?.status === ContextStatusType.LOGIN_IN_PROGRESS) {
        this.logService.log(
          `Token expired but login already in progress — skipping redirect.`,
          'warning',
          `HTTP -> TOKEN-EXPIRED`
        );
        return;
      }

      const userFlowRequest = {
        scopes: ['openid'],
        authority: this.b2cPolicies.authorities.signIn.authority,
      };

      this.logService.log(
        `Session expired. Logout and clear context for TOKEN[${token}].`,
        'warning',
        `HTTP -> TOKEN-EXPIRED`
      );

      // Logout, clear session and global contexts
      this.userContextService.logout();
      this.userContextService.updateContext(ContextUpdateType.STATUS, ContextStatusType.LOGIN_IN_PROGRESS);

      this.store.dispatch(new SetSelectedRoute('home'));
      this.store.dispatch(new AzureLogin(userFlowRequest));
    }
  }

  // checkOrRefreshToken(token: string) {
  //   const savedTokenExipry = JSON.parse(sessionStorage.getItem(token));
  //   if (savedTokenExipry < Math.floor(Date.now() / 1000)) {
  //     // Refresh token if expired
  //     const userFlowRequest = {
  //       scopes: ['openid'],
  //       authority: this.b2cPolicies.authorities.signIn.authority
  //     };
  //     sessionStorage.removeItem(savedTokenExipry);
  //     this.store.dispatch(new SetSelectedRoute('home'));
  //     this.store.dispatch(new AzureLogin(userFlowRequest));
  //   }
  // }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    req = this.configureRequest(req);

    return next.handle(req).pipe(
      tap(evt => {
        this.handleSuccessResponse(evt, req);
      }),
      catchError((err: any) => {
        return this.handleErrorResponse(err);
      })
    );
  }

  private configureRequest(req: HttpRequest<any>): HttpRequest<any> {
    let newUrl;

    if (Object(isDevMode)() && req.url.indexOf('assets') === -1) {
      return this.configureDevModeRequest(req);
    }

    if (!Object(isDevMode)() && req.url.indexOf('/api') !== -1) {
      return this.configureProdModeRequest(req);
    }

    return req.clone({
      withCredentials: true,
      url: req.url,
    });
  }

  private configureDevModeRequest(req: HttpRequest<any>): HttpRequest<any> {
    const newUrl = this.apiUrl + req.url;
    return req.clone({
      withCredentials: false,
      url: newUrl,
      headers: new HttpHeaders({
        ...this.getAdditionalHeaders(req),
      }),
    });
  }

  private configureProdModeRequest(req: HttpRequest<any>): HttpRequest<any> {
    this.ensureAuthDataAvailable();

    return req.clone({
      withCredentials: true,
      url: req.url,
      headers: new HttpHeaders({
        Authorization: `${this.authData.token_type} ${this.authData.access_token}`,
        ...this.getAdditionalHeaders(req),
      }),
    });
  }

  private ensureAuthDataAvailable(): void {
    if (!this.authData || !this.authData.access_token) {
      const tokenInfo = this.userContextService.getTokenInfo();

      if (!tokenInfo || !tokenInfo.authData || !tokenInfo.authData.access_token) {
        this.logService.log(
          'Authorization access token was not found in Global Context. Unable to login',
          'error',
          `HTTP INT -> AUTH ERROR  🚨 🚨 🚨`
        );
        return;
      }

      this.logService.log(
        'Authorization access token was not found. Retrieving authorization data from Global Context...',
        'warning',
        `USER_CONTEXT -> AUTH WARNING ⏰`
      );
      this.logService.log(
        `Auth data: \n${JSON.stringify(tokenInfo.authData, null, '\t')}`,
        'info',
        'HTTP INT -> AUTH DATA'
      );

      this.authData = tokenInfo.authData;
      this.store.dispatch(new SetAuthData(tokenInfo.authData));
    }
  }

  private handleSuccessResponse(evt: HttpEvent<any>, req: HttpRequest<any>): void {
    if (evt instanceof HttpResponse && req.body && req.body.notify) {
      this.store.dispatch(
        new SetNotification({
          error: false,
          warning: false,
          message: 'Action successful',
          title: 'Success',
        })
      );
    }
  }

  private handleErrorResponse(err: any): Observable<any> {
    this.progressDialogService.hideSpinner();
    const errDetail = err.error?.details;

    if (err instanceof HttpErrorResponse) {
      this.processHttpError(err, errDetail);
    }

    return of(err);
  }

  private processHttpError(err: HttpErrorResponse, errDetail: any): void {
    try {
      let errorMessage = this.extractErrorMessage(err, errDetail);

      if (errorMessage.toLowerCase().indexOf('password') !== -1) {
        this.store.dispatch(new SetSelectedRoute('login'));
      }

      this.handleErrorByStatus(err, errorMessage);
    } catch (e) {
      this.logService.log(`Error processing HTTP error: ${e}`, 'error', 'HTTP INT -> ERROR');
      this.dispatchUnknownError();
    }
  }

  private extractErrorMessage(err: HttpErrorResponse, errDetail: any): string {
    let errorMessage = errDetail ?? err.message;

    if (Array.isArray(err.error)) {
      errorMessage = '';
      err.error.forEach(errorMessageVal => (errorMessage += `${errorMessageVal.msg + '\n'}`));
    }

    return errorMessage;
  }

  private handleErrorByStatus(err: HttpErrorResponse, errorMessage: string): void {
    switch (err.status) {
      case 400:
        this.handle400Error(err);
        break;
      case 401:
        this.handleAuthenticationError(true);
        break;
      case 403:
        this.handleAuthenticationError();
        break;
      case 404:
        this.handle404Error(err);
        break;
      case 408:
        this.store.dispatch(new SetSelectedRoute('console'));
        break;
      case 0:
        this.dispatchUnknownError();
        break;
      case 502:
        this.handle502Error(err);
        break;
      default:
        this.handleDefaultError(err);
        break;
    }
  }

  private handle400Error(err: HttpErrorResponse): void {
    const errorMessage = err.error.details ?? err.error.message ?? AlertMessages['400'];

    this.store.dispatch(
      new SetNotification({
        error: true,
        warning: false,
        message: errorMessage,
        title: 'Error',
        duration: 0,
      })
    );
  }

  private handleAuthenticationError(clearStorage: boolean = false): void {
    const context = this.userContextService.getContext();

    // Do not stack another redirect if login is already in progress.
    if (context.global?.status === ContextStatusType.LOGIN_IN_PROGRESS) {
      this.logService.log(
        '401/403 received but login already in progress — skipping duplicate redirect.',
        'warning',
        'USER_CONTEXT -> LOGIN_FLOW  ⏰'
      );
      return;
    }

    this.logService.log('Re-login required', 'warning', 'USER_CONTEXT -> LOGIN_FLOW  ⏰');
    this.userContextService.updateContext(ContextUpdateType.STATUS, ContextStatusType.LOGIN_IN_PROGRESS);

    const userFlowRequest = {
      scopes: ['openid'],
      authority: this.b2cPolicies.authorities.signIn.authority,
      prompt: 'login',
    };

    if (clearStorage) {
      this.userContextService.clearAllBrowserStorage();
    }

    this.store.dispatch(new AzureLogin(userFlowRequest));
  }

  private handle404Error(err: HttpErrorResponse): void {
    if (err.url.includes('api/ext/users')) {
      return;
    }

    if (!err.url.includes('update-management')) {
      this.store.dispatch(new SetSelectedRoute('home'));
    }

    this.store.dispatch(
      new SetNotification({
        error: true,
        warning: false,
        message: err.error.details ?? err.error.message ?? AlertMessages['404'],
        title: 'Error',
        duration: 0,
      })
    );
  }

  private dispatchUnknownError(): void {
    this.store.dispatch(
      new SetNotification({
        error: true,
        warning: false,
        message: 'Unknown Error, Please try again or contact helpdesk',
        title: 'Error',
        duration: 0,
      })
    );
  }

  private handle502Error(err: HttpErrorResponse): void {
    this.store.dispatch(new SetSelectedRoute('console'));
    this.store.dispatch(
      new SetNotification({
        error: true,
        warning: false,
        message: err.error.details ?? err.error.message ?? AlertMessages['502'],
        title: 'Error',
        duration: 0,
      })
    );
  }

  private handleDefaultError(err: HttpErrorResponse): void {
    const errMessage = err.error.details?.includes('Internal Server Error')
      ? AlertMessages.HttpErrorResponse
      : (err.error.message ?? err.error.details ?? AlertMessages.HttpErrorResponse);

    this.store.dispatch(
      new SetNotification({
        error: true,
        warning: false,
        message: errMessage,
        title: err.name,
        duration: 0,
      })
    );
  }

  ngOnDestroy(): void {
    this.destroyed.next('');
    this.destroyed.complete();
  }
}
